import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { device } from '@/common/constants/device'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import CustomTooltip from '@/components/ui/CustomTooltip'
import { useTranslations } from 'next-intl'

const Wrap = styled.div`
  display: flex;
  justify-content: center;
`

const Tab = styled.button.attrs(({ className }) => ({
  className,
}))`
  padding: 12px 8px;
  position: relative;
  flex: 1 0 auto;
  font-weight: 500;
  font-size: 14px;
  text-align: center;
  line-height: 1.15;
  letter-spacing: -0.015em;
  color: #7a7a7a;
  border-bottom: 4px solid #e9e9e9;
  background: none;

  &.active {
    border-color: #3f3f49;
    color: #3f3f49;
  }

  @media ${device.tablet} {
    font-size: 18px;
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &.purple {
    border-bottom: 2px solid #e9e9e9;

    &.active {
      border-color: #6a67da;
    }
  }
`

const DisabledTab = styled.div`
  padding: 12px 8px;
  position: relative;
  flex: 1 0 auto;
  font-weight: 500;
  font-size: 14px;
  text-align: center;
  line-height: 1.15;
  letter-spacing: -0.015em;
  color: #7a7a7a;
  border-bottom: 4px solid #e9e9e9;
  background: none;
  opacity: 0.5;
  pointer-events: none;
  cursor: default;

  &.active {
    border-color: #3f3f49;
    color: #3f3f49;
  }

  @media ${device.tablet} {
    font-size: 18px;
  }

  &.purple {
    border-bottom: 2px solid #e9e9e9;

    &.active {
      border-color: #6a67da;
    }
  }
`

const Tabs = ({
  tabs,
  cbFunc,
  className,
  activeIndex = 0,
  tabsType,
  tag = 'button',
  tooltipText = '',
  fileName = '',
}) => {
  const { pathname } = useRouter()
  const t = useTranslations(fileName)
  const [activeBtn, setActiveBtn] = useState(activeIndex)

  useEffect(() => {
    setActiveBtn(activeIndex)
  }, [activeIndex])

  const openTab = (e) => {
    const newIndex = +e.target.dataset.index
    setActiveBtn(newIndex)

    if (cbFunc) {
      cbFunc(e.target.dataset.name)
    }
  }

  const getCurrentTabAttribute = (data) => {
    if (tag === 'button') {
      data.attribute = {}
      data.attribute.type = 'button'
    }

    return data.attribute ?? {}
  }

  const getTabClasses = (data, i) => {
    let isActive = activeBtn === i

    if (tag !== 'button') {
      isActive = pathname === data.attribute.href
    }

    return classNames('tabs__tab', tabsType, {
      active: isActive,
    })
  }

  const isTabDisabled = (data) => {
    const isReachTab = data.value === 'reach'
    const isReachDataMissing = !data.reachData
    return data.isDisabled || (isReachTab && isReachDataMissing)
  }

  return (
    <Wrap className={className}>
      {tabs.map((data, i) => {
        const isDisabled = isTabDisabled(data)
        const key = data.value

        const tabContent = isDisabled ? (
          <DisabledTab
            className={`${getTabClasses(
              data,
              i
            )} tabs-tab disabled ${tabsType}`}
          >
            {t(data.label)}
          </DisabledTab>
        ) : (
          <Tab
            className={`${getTabClasses(data, i)} tabs-tab ${
              data.isDisabled ? 'disabled' : ''
            }`}
            as={tag}
            key={key}
            onClick={openTab}
            disabled={data.isDisabled}
            data-index={i}
            data-name={data.value}
            {...getCurrentTabAttribute(data, i)}
          >
            {t(data.label)}
          </Tab>
        )

        if (isDisabled) {
          return (
            <CustomTooltip
              id={`creativeFavoriteTooltip-${i}`}
              effect="solid"
              place="top"
              delayShow={200}
              delayHide={50}
              tooltipTitle={t(tooltipText)}
              key={key}
            >
              {tabContent}
            </CustomTooltip>
          )
        } else if (data.tooltipTitle) {
          return (
            <CustomTooltip
              id={`customTabTooltip-${i}`}
              effect="solid"
              place="top"
              delayShow={200}
              delayHide={50}
              tooltipTitle={data.tooltipTitle}
              tooltipContent={data.tooltipContent}
              iconName="info-circle"
              key={key}
            >
              {tabContent}
            </CustomTooltip>
          )
        }

        return <React.Fragment key={key}>{tabContent}</React.Fragment>
      })}
    </Wrap>
  )
}

export default Tabs
